import { useEffect, useMemo, useRef } from 'react'
import { Box, Skeleton, Text } from '@holbonkit/ui'
import { useInfiniteQuery } from '@tanstack/react-query'

import {
    listSelfEmployedTransactions,
    listSelfEmployedTransactionsQueryKey,
    type ListSelfEmployedTransactionsQueryResponse,
} from '@/shared/api'

import { groupTransactionsByDate, type THistoryDay } from '../../model/group-transactions-by-date'
import { OperationsItem } from '../operations-item/operations-item'

import styles from './history.module.scss'

const PAGE_LIMIT = 20

export const History = () => {
    const listRef = useRef<HTMLDivElement>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)

    const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isPending } =
        useInfiniteQuery<ListSelfEmployedTransactionsQueryResponse>({
            queryKey: listSelfEmployedTransactionsQueryKey({ limit: PAGE_LIMIT }),

            queryFn: ({ pageParam, signal }) =>
                listSelfEmployedTransactions(
                    {
                        limit: PAGE_LIMIT,
                        cursor: pageParam as string | undefined,
                    },
                    { signal },
                ),
            initialPageParam: undefined as string | undefined,
            getNextPageParam: (lastPage) =>
                lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
        })

    const pages = data?.pages
    const days = useMemo(
        () => groupTransactionsByDate(pages?.flatMap((page) => page.transactions) ?? []),
        [pages],
    )

    useEffect(() => {
        const root = listRef.current
        const sentinel = sentinelRef.current

        if (!root || !sentinel) {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage && !isError) {
                    void fetchNextPage()
                }
            },
            { root, rootMargin: '160px' },
        )

        observer.observe(sentinel)

        return () => observer.disconnect()
    }, [days.length, fetchNextPage, hasNextPage, isError, isFetchingNextPage])

    const renderHistory = () => {
        if (isPending) {
            return (
                <Box className={styles['history__status']}>
                    <Skeleton style={{ height: 56, borderRadius: 8 }} />
                    <Skeleton style={{ height: 56, borderRadius: 8 }} />
                    <Skeleton style={{ height: 56, borderRadius: 8 }} />
                </Box>
            )
        }

        if (isError && days.length === 0) {
            return (
                <Text className={styles['history__status']} variant='body-m-16-medium'>
                    Не удалось загрузить историю
                </Text>
            )
        }

        if (days.length === 0) {
            return (
                <Text className={styles['history__status']} variant='body-m-16-medium'>
                    Операций пока нет
                </Text>
            )
        }

        return (
            <>
                {days.map((day) => (
                    <HistoryDay key={day.dateKey} day={day} />
                ))}
                {isFetchingNextPage ?
                    <Box className={styles['history__status']}>
                        <Skeleton style={{ height: 56, borderRadius: 8 }} />
                    </Box>
                :   null}
                {isError ?
                    <Text className={styles['history__status']} variant='body-m-16-medium'>
                        Не удалось загрузить ещё операции
                    </Text>
                :   null}
            </>
        )
    }

    return (
        <div ref={listRef} className={styles['history']}>
            {renderHistory()}
            <div ref={sentinelRef} aria-hidden />
        </div>
    )
}

const HistoryDay = ({ day }: { day: THistoryDay }) => {
    return (
        <Box className={styles['history__day']}>
            <Text variant='body-l-18-bold'>{day.title}</Text>
            <Box className={styles['history__day--operations']}>
                {day.transactions.map((transaction) => (
                    <OperationsItem
                        key={transaction.id}
                        amount={transaction.amount}
                        date={transaction.created_at}
                        id={transaction.id}
                        status={transaction.status}
                        type={transaction.type}
                    />
                ))}
            </Box>
        </Box>
    )
}
